import React, { useEffect } from 'react';
import { CalendarDays, ArrowLeft, Clock, ChevronRight, HelpCircle } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import Layout from '../components/Layout';
import NotFound from './NotFound';
import { useApp } from '../context/AppContext';
import { findPost, blogSection, blogPosts } from '../mock/content';
import { buildArticle } from '../mock/blogContent';
import useSEO from '../hooks/useSEO';

const slugId = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

const BlogArticle = () => {
  const { slug } = useParams();
  const { lang } = useApp();
  const s = blogSection[lang] || blogSection.en;
  const post = findPost(slug);
  const loc = post ? (post[lang] || post.en) : null;
  useSEO(loc ? `${loc.title} | CAB.TAXI Zürich` : 'Blog', loc ? loc.excerpt : '', { image: post ? post.img : undefined, skip: !post });
  useEffect(() => {
    if (!post || !loc) return undefined;
    const s = document.createElement('script');
    s.type = 'application/ld+json';
    s.text = JSON.stringify({ '@context': 'https://schema.org', '@type': 'Article', headline: loc.title, description: loc.excerpt, image: `${window.location.origin}${post.img}`, datePublished: post.date || undefined, inLanguage: lang,
      author: { '@type': 'Organization', name: 'CAB.TAXI' }, publisher: { '@type': 'Organization', name: 'CAB.TAXI', logo: { '@type': 'ImageObject', url: `${window.location.origin}/logo.png` } }, mainEntityOfPage: window.location.href });
    document.head.appendChild(s);
    return () => s.remove();
  }, [post, loc, lang]);

  if (!post) {
    return <NotFound />;
  }

  const art = buildArticle(post, lang);
  const cta = { de: 'Jetzt Transfer buchen', en: 'Book your transfer now', fr: 'Réservez votre transfert', it: 'Prenota il tuo transfer' }[lang] || 'Book your transfer now';
  const ctaBtn = { de: 'Zum Festpreis buchen', en: 'Book at fixed price', fr: 'Réserver à prix fixe', it: 'Prenota a prezzo fisso' }[lang] || 'Book at fixed price';
  const moreLabel = { de: 'Weitere Artikel', en: 'More articles', fr: 'Autres articles', it: 'Altri articoli' }[lang] || 'More articles';
  const related = blogPosts.filter((p) => p.slug !== post.slug).slice(0, 3);

  return (
    <Layout bookingTop>
      <article className="max-w-3xl mx-auto px-4 sm:px-6 py-10" data-testid="blog-article">
        <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-500 hover:text-[color:var(--brand-dark)] mb-6"><ArrowLeft className="w-4 h-4" /> {s.back}</Link>
        <span className="text-[11px] font-bold px-2.5 py-1 rounded-full text-white" style={{ backgroundColor: 'var(--brand-accent)' }}>{post.tag}</span>
        <h1 className="mt-4 text-3xl sm:text-4xl font-extrabold text-[color:var(--brand-dark)] leading-tight">{loc.title}</h1>
        <div className="mt-3 flex flex-wrap items-center gap-4 text-sm text-gray-400">
          <span className="flex items-center gap-1.5"><CalendarDays className="w-4 h-4" /> {post.date}</span>
          <span className="flex items-center gap-1.5" data-testid="blog-readtime"><Clock className="w-4 h-4" /> {art.readMin} {art.labels.min}</span>
        </div>
        <img src={post.img} alt={loc.title} className="mt-6 rounded-2xl w-full h-72 object-cover" />

        {/* Table of contents */}
        <nav className="mt-8 rounded-2xl bg-[color:var(--brand-muted)] p-5" data-testid="blog-toc">
          <div className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-3">{art.labels.toc}</div>
          <ul className="space-y-1.5">
            {art.sections.map((sec) => (
              <li key={sec.h}>
                <a href={`#${slugId(sec.h)}`} className="inline-flex items-center gap-1.5 text-sm font-semibold text-[color:var(--brand-dark)] hover:text-[color:var(--brand-accent)] transition-colors">
                  <ChevronRight className="w-4 h-4" style={{ color: 'var(--brand-accent)' }} /> {sec.h}
                </a>
              </li>
            ))}
            <li>
              <a href="#faq" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[color:var(--brand-dark)] hover:text-[color:var(--brand-accent)] transition-colors">
                <ChevronRight className="w-4 h-4" style={{ color: 'var(--brand-accent)' }} /> {art.labels.faq}
              </a>
            </li>
          </ul>
        </nav>

        {/* Intro */}
        <div className="mt-8 space-y-5 text-[15px] leading-relaxed text-gray-700">
          {art.intro.map((para, i) => <p key={i}>{para}</p>)}
        </div>

        {/* Sections */}
        <div className="mt-8 space-y-9">
          {art.sections.map((sec) => (
            <section key={sec.h} id={slugId(sec.h)} className="scroll-mt-24" data-testid={`blog-section-${slugId(sec.h)}`}>
              <h2 className="text-xl sm:text-2xl font-extrabold text-[color:var(--brand-dark)] mb-3">{sec.h}</h2>
              <div className="space-y-4 text-[15px] leading-relaxed text-gray-700">
                {sec.paras.map((p, i) => <p key={i}>{p}</p>)}
              </div>
            </section>
          ))}
        </div>

        {/* FAQ */}
        <section id="faq" className="mt-12 scroll-mt-24" data-testid="blog-faq">
          <h2 className="text-xl sm:text-2xl font-extrabold text-[color:var(--brand-dark)] mb-4 flex items-center gap-2">
            <HelpCircle className="w-6 h-6" style={{ color: 'var(--brand-accent)' }} /> {art.labels.faq}
          </h2>
          <div className="space-y-3">
            {art.faqs.map((f) => (
              <details key={f.q} className="group rounded-xl border border-gray-100 bg-white p-4">
                <summary className="cursor-pointer list-none font-bold text-[color:var(--brand-dark)] flex items-center justify-between gap-2">
                  {f.q}
                  <ChevronRight className="w-4 h-4 shrink-0 transition-transform group-open:rotate-90" style={{ color: 'var(--brand-accent)' }} />
                </summary>
                <p className="mt-2 text-sm text-gray-600 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        {/* CTA */}
        <div className="mt-10 rounded-2xl brand-gradient p-8 text-center">
          <h3 className="text-xl font-extrabold text-white">{cta}</h3>
          <Link to="/booking" className="mt-4 inline-block accent-btn rounded-full px-6 py-3 font-bold text-sm" data-testid="blog-cta-book">{ctaBtn}</Link>
        </div>

        {/* Related */}
        <div className="mt-12">
          <h3 className="text-sm font-bold uppercase tracking-widest text-gray-400 mb-4">{moreLabel}</h3>
          <div className="grid sm:grid-cols-3 gap-4">
            {related.map((r) => {
              const rl = r[lang] || r.en;
              return (
                <Link key={r.slug} to={`/blog/${r.slug}`} className="card-hover group rounded-xl overflow-hidden bg-white border border-gray-100" data-testid={`blog-related-${r.slug}`}>
                  <img src={r.img} alt={rl.title} className="w-full h-24 object-cover" />
                  <div className="p-3 text-sm font-bold text-[color:var(--brand-dark)] leading-snug">{rl.title}</div>
                </Link>
              );
            })}
          </div>
        </div>
      </article>
    </Layout>
  );
};

export default BlogArticle;
